/**
 * Swamithra — server-driven content.
 *
 * Today that is one thing: the Personalised Services banner (US-00-005). It is
 * server-side configuration rather than a constant in three bundles, because
 * changing the contact number must not need a web deploy and an app-store
 * release (US-00-005.7).
 *
 * WHAT THIS ENDPOINT MUST NOT DO. It carries no user-identifying data, takes no
 * query, vetting or profile identifier, and answers with or without a session
 * (US-00-005.12). It is also the reason there is no booking, scheduling, payment
 * or pricing schema anywhere in this package — US-00-005.6 requires that no such
 * endpoint exists for a client to call, and the absence is easier to audit when
 * it is stated.
 */

import { z } from "zod"

import { isValidMobileNumber } from "./phone"

/**
 * How long a fetched banner stays fresh before it is refetched.
 *
 * Fifteen minutes: long enough that the banner is effectively free on a list
 * screen, short enough that an operator's change appears the same working hour
 * (US-00-005.7).
 */
export const PERSONALISED_SERVICES_CACHE_TTL_MS = 15 * 60 * 1000

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * The banner as served.
 *
 * Both contact fields are OPTIONAL. A stored value that is not valid E.164 or
 * not a valid address is omitted by the server and logged there (US-00-005.15);
 * the client's job is to render what remains and suppress the banner when
 * nothing valid is left.
 *
 * The client re-checks validity rather than trusting the omission, because this
 * value ends up in a `tel:` or `mailto:` link and a malformed one produces a
 * dialler full of rubbish rather than an error.
 */
const contentFieldsSchema = z.object({
	headline: z.string().min(1),
	// `nullish`, accepting BOTH an omitted key and an explicit null. The criterion
	// says the invalid field is omitted (US-00-005.15); an explicit null for a
	// known field with no value is equally defensible. Accepting either costs
	// nothing and means the banner does not disappear because the two were read
	// differently.
	contactMobileNumber: z.string().nullish(),
	contactEmail: z.string().nullish(),
})

/**
 * The banner as served.
 *
 * THE SERVICE CALLS THE TWO CONTACT FIELDS `mobile_number` AND `email`. This
 * package calls them `contactMobileNumber` and `contactEmail`, because on a
 * screen full of the signed-in user's own details "the mobile number" is
 * ambiguous and the banner's is not theirs. The service's spelling is the
 * contract, so it is accepted here and mapped once — the alternative, which is
 * what shipped, is a `.nullish()` field that silently reads `undefined` from
 * every response and a banner that never renders on any platform.
 */
export const personalisedServicesSchema = z.preprocess(value => {
	if (value === null || typeof value !== "object") return value
	const record = value as Record<string, unknown>
	// `in`, not `??`: an explicit null is a value the service chose to send and
	// must not fall through to the other spelling of the same field.
	return {
		...record,
		contactMobileNumber:
			"contactMobileNumber" in record ? record.contactMobileNumber : record.mobileNumber,
		contactEmail: "contactEmail" in record ? record.contactEmail : record.email,
	}
}, contentFieldsSchema)

export type PersonalisedServicesContent = z.infer<typeof personalisedServicesSchema>

/**
 * The number, if it is dialable. Null otherwise — never a half-valid link.
 *
 * `isValidMobileNumber`, not bare E.164 syntax: a stored `+91984501234` is one
 * digit short of an Indian number and the service has been observed to serve it.
 * The client re-checks because this value ends up in a `tel:` link, and a
 * dialler prefilled with a number that cannot ring is worse than no banner
 * (US-00-005.15).
 */
export function validContactNumber(content: PersonalisedServicesContent): string | null {
	return content.contactMobileNumber && isValidMobileNumber(content.contactMobileNumber)
		? content.contactMobileNumber
		: null
}

/** The address, if it is mailable. */
export function validContactEmail(content: PersonalisedServicesContent): string | null {
	return content.contactEmail && EMAIL_RE.test(content.contactEmail) ? content.contactEmail : null
}

/**
 * Whether the banner renders at all.
 *
 * False means OMIT IT — not render an empty contact block (US-00-005.9,
 * US-00-005.15). A banner headed "Want personalised assistance?" with no way
 * to make contact is worse than no banner.
 */
export function shouldRenderBanner(content: PersonalisedServicesContent | null): boolean {
	if (!content) return false
	return validContactNumber(content) !== null || validContactEmail(content) !== null
}

/**
 * Accessible labels for the two tap targets (US-00-005.14).
 *
 * Here rather than in each app so the screen-reader wording, which is part of
 * the acceptance criterion, is written once.
 */
export const CONTACT_NUMBER_A11Y_LABEL = "Personalised Services contact number"
export const CONTACT_EMAIL_A11Y_LABEL = "Personalised Services contact email"

/**
 * The line under the banner's heading, on every client. The heading itself is
 * the server's `headline` (configuration on the API).
 */
export const PERSONALISED_SERVICES_DESCRIPTION = "Speak to the Swamithra team directly."

/** When the contact number is answered, shown wherever a call option is. */
export const CONTACT_CALL_HOURS = "Monday-Saturday. 10:00 am to 5:00 pm"

/** Minimum tap-target size on mobile, in points (US-00-005.14). */
export const MIN_TAP_TARGET_PT = 44
